import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import { FormLabel } from "@ecco/ui/form-label";
import { FormHelperText } from "@ecco/ui/form-helper-text";
import { Input } from "@ecco/ui/input";
import { styles } from "./TextInputField.styles";
import type { SingleFieldProps } from "../../../commerce/baseForms/baseFormsValidations";
import { InputGroup } from "@ecco/ui/input-group";
import { InputRightElement } from "@ecco/ui/input-element";
import type { ComponentProps, InputHTMLAttributes, ReactElement } from "react";
import { forwardRef } from "react";
import type { ECCOIconProps } from "@ecco/ui/ecco-icon";
import { ECCOIcon } from "@ecco/ui/ecco-icon";

export type TextInputFieldProps = InputHTMLAttributes<HTMLInputElement> & {
    error?: string;
    header: string;
    helpText?: string;
    rightIcon?: ReactElement<any> | ECCOIconProps["name"];
    isInvalid?: boolean;
    isSuccess?: boolean;
    isDisabled?: boolean;
    htmlId?: string;
    isReadOnly?: boolean;
    testId?: string;
    // This causes a clash because InputHTMLAttributes also has size defined which doesn't match the Input props.
    size?: ComponentProps<typeof Input>["size"];
};

export const TextInputField = forwardRef<HTMLInputElement, TextInputFieldProps>(
    (
        {
            error,
            isInvalid,
            isSuccess,
            header,
            required = true,
            isReadOnly = false,
            rightIcon,
            testId,
            helpText,
            ...rest
        },
        ref
    ) => {
        const iconProps = {
            rightIcon,
            isInvalid,
            isSuccess,
        };

        return (
            <FormControl
                isInvalid={isInvalid}
                isRequired={required}
                isReadOnly={isReadOnly}
                isSuccess={isSuccess}
            >
                <FormLabel sx={styles.label}>{header}</FormLabel>
                <InputGroup>
                    <Input
                        variant="outline"
                        ref={ref}
                        data-testid={testId}
                        sx={styles.input}
                        {...rest}
                    />
                    {rightIcon && (
                        <InputRightElement h={"100%"}>
                            <FieldIcon {...iconProps} />
                        </InputRightElement>
                    )}
                </InputGroup>
                {helpText && (
                    <FormHelperText fontSize={"mobileBodyB4"} pt={2} size={"tiny"} color="black">
                        {helpText}
                    </FormHelperText>
                )}
                <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>
        );
    }
);

TextInputField.displayName = "TextInputField";

export const FieldIcon = ({
    rightIcon,
    isInvalid,
    isSuccess,
    ariaLabel,
}: Pick<SingleFieldProps, "rightIcon" | "isInvalid" | "isSuccess" | "ariaLabel">) => {
    const getIconStyles = () => {
        if (isSuccess) {
            return styles.icon.success;
        }
        if (isInvalid) {
            return styles.icon.invalid;
        }
        return {};
    };

    if (typeof rightIcon === "string") {
        return (
            <ECCOIcon
                name={rightIcon}
                size="sm"
                aria-label={ariaLabel}
                aria-hidden={!ariaLabel}
                sx={{ ...getIconStyles() }}
            />
        );
    }

    return rightIcon;
};

export default TextInputField;
